// Systemic Formulas "Holobiome Roadmap" — the five sequential kits (the 5 P's)
// and the products in each. Source of truth is the "Section Supplements" tab of
// the LabSmarts patterns sheet (gid 586166022): the kit X-matrix at column N,
// and the per-product Holobiome Kit column (master col H). Encoded here so the
// Supplement Protocols panel can group recommended supplements by kit without a
// runtime sheet fetch.
//
// productNames use the master supplement TITLE exactly as
// FurtherConsiderations.resolveSupplement() returns it (e.g. "Immune Superfood",
// "Clear 1 µBiomic"), so an active-supplement match is a plain name compare that
// unifies a product's primary and "-2" tag variants (both resolve to the same
// title). Six products (LymphGenic, the three Neuro's, VitaGenic, OrganGenic)
// don't yet have a catalog tag, so no pattern can recommend them — they render
// as always-inactive members of their kit until they're wired up.

import { Section } from './Section';

// The brand the roadmap belongs to. EVERYTHING Holobiome is scoped to it — the
// kits, the product table under them, and the report section that holds both —
// so unchecking "Systemic Formulas" in Supplement Settings takes the whole
// section away. Lives here rather than in either panel so the two can't drift.
export const SYSTEMIC_FORMULAS_BRAND = 'systemicFormulas';

// The Holobiome Roadmap's own "Include in PDF" key. A pseudo-section, the way
// MVX_SECTION is in bloodwork-test-analysis.tsx: it isn't a body system, it just
// needs a stable string in excludedFromPdfSections. It gets its own key rather
// than sharing Section.SUPPLEMENTS because the default profile enables Systemic
// Formulas ONLY — with no second brand checked the Supplements panel renders
// nothing, and its checkbox (the one that would have controlled the roadmap
// too) wouldn't be on screen to uncheck.
export const HOLOBIOME_SECTION = 'Holobiome Roadmap' as unknown as Section;

export type HolobiomeKitKey =
  | 'prepare'
  | 'purify'
  | 'promote'
  | 'protect'
  | 'preserve';

export interface HolobiomeKit {
  key: HolobiomeKitKey;
  // Roadmap order (1-based). The kits are a sequential monthly protocol, so this
  // doubles as the "Month N" in the card title (Prepare → Preserve).
  month: number;
  name: string;
  // Short descriptor shown under the "Month N: <name>" title (e.g. the Prepare
  // card reads "Month 1: Prepare" / "Drainage & Energy"). '' renders no subtitle.
  subtitle: string;
  // Solid color for an ACTIVE product chip; paired with white text.
  color: string;
  productNames: string[];
}

export const HOLOBIOME_KITS: HolobiomeKit[] = [
  {
    key: 'prepare',
    month: 1,
    name: 'Prepare',
    subtitle: 'Drainage & Energy',
    color: '#2f6f7e',
    productNames: ['EnerGenic', 'MethylGenic', 'ImmuneGenic', 'LymphGenic', 'MucosaGenic'],
  },
  {
    key: 'purify',
    month: 2,
    name: 'Purify',
    subtitle: 'Detox & Gut Health',
    color: '#3f7a55',
    productNames: ['Clear 1 µBiomic', 'Clear 2 µBiomic', 'Terra µBiomic', 'Terra Superfood', 'DetoxiGenic', 'BindGenic'],
  },
  {
    key: 'promote',
    month: 3,
    name: 'Promote',
    subtitle: 'Immunity & Microbiome',
    color: '#9c6a1e',
    productNames: ['ImmuneGenic', 'MucosaGenic', 'Immune µBiomic', 'Immune Superfood', 'CellGenic'],
  },
  {
    key: 'protect',
    month: 4,
    name: 'Protect',
    subtitle: 'Neurological Health',
    color: '#6f4f89',
    productNames: ['Neuro µBiomic', 'NeuroGenic', 'Neuro Superfood', 'CalmGenic', 'EpiGenic'],
  },
  {
    key: 'preserve',
    month: 5,
    name: 'Preserve',
    subtitle: 'Maintenance',
    color: '#965a34',
    productNames: ['Terra µBiomic', 'Terra Superfood', 'CellGenic', 'VitaGenic', 'OrganGenic'],
  },
];

// Normalize a supplement name for matching (case/whitespace-insensitive). The
// micro sign (µ, U+00B5) and Greek mu (μ, U+03BC) both appear in the wild for
// "µBiomic"; fold them so a product name always matches its recommendation.
export const normalizeSupplementName = (name: string): string =>
  (name || '')
    .trim()
    .toLowerCase()
    .replace(/μ/g, 'µ')
    .replace(/\s+/g, ' ');
